import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState, LoginCredentials } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
  });

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = await authAPI.getCurrentUser();
          setState({
            isAuthenticated: true,
            user: userData,
            loading: false,
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
          localStorage.removeItem('token');
          setState({
            isAuthenticated: false,
            user: null,
            loading: false,
          });
        }
      } else {
        setState({
          isAuthenticated: false,
          user: null,
          loading: false,
        });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const { frontendLogger } = await import('../utils/logger');
      frontendLogger.logAuthOperation('login_attempt', {
        has_username: !!credentials.username
      });
      
      const response = await authAPI.login(
        credentials.username,
        credentials.password
      );
      
      if (!response.access_token) {
        frontendLogger.error('No access token received', 'AuthError');
        throw new Error('No access token received');
      }
      
      localStorage.setItem('token', response.access_token);
      
      // Fetch the actual user data
      const userData = await authAPI.getCurrentUser();
      frontendLogger.logAuthOperation('login_success', {
        user_id: userData?.id
      });
      
      // Set authentication state with the fetched user data
      setState({
        isAuthenticated: true,
        user: userData,
        loading: false,
      });
      
      return;
    } catch (error) {
      const { frontendLogger } = await import('../utils/logger');
      frontendLogger.error('Login failed', 'LoginError', {
        error_message: (error as Error)?.message
      });
      
      // Clear any existing token
      localStorage.removeItem('token');
      // Reset state
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
      });
      throw error;
    }
  };

  const logout = async () => {
    const { frontendLogger } = await import('../utils/logger');
    frontendLogger.logAuthOperation('logout');
    
    localStorage.removeItem('token');
    setState({
      isAuthenticated: false,
      user: null,
      loading: false,
    });
  };

  if (state.loading) {
    return <div>Loading...</div>; // You can replace this with a proper loading component
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 